import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useEventTracking } from '../hooks/useAnalytics';
import Navbar from './Navbar';

const HeroSlider = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();
    const { trackServiceInquiry } = useEventTracking();

    const slides = [
        {
            id: 0,
            title: "Professional Insulation Services",
            subtitle: "Keep your home warm and energy-efficient",
            description: "Expert insulation installation for ceilings, walls, and underfloor. Save energy and stay comfortable year-round.",
            image: require('../images/insulation-1.avif'),
            ctaText: "Get Free Assessment",
            ctaLink: "/contact",
            service: "Insulation"
        },
        {
            id: 1,
            title: "Quality Garage Door Solutions",
            subtitle: "Security and convenience for your home",
            description: "Professional garage door installation, repair, and maintenance services. Secure your property with style.",
            image: require('../images/garage-1.jpg'),
            ctaText: "Request Quote",
            ctaLink: "/contact",
            service: "Garage Doors"
        },
        {
            id: 2,
            title: "Comprehensive Home Health Inspection",
            subtitle: "Ensure your family's safety and well-being",
            description: "Professional home health assessments to identify potential issues and ensure your property meets safety standards.",
            image: require('../images/hero-image.jpg'),
            ctaText: "Schedule Inspection",
            ctaLink: "/contact",
            service: "Healthy Home Inspection"
        }
    ];

    // Auto-play functionality
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000); // Change slide every 5 seconds

        return () => clearInterval(timer);
    }, [slides.length]);

    // Scroll detection for navbar behavior
    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    setIsScrolled(window.scrollY > 100);
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    const goToPrevious = () => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    };

    const goToNext = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const handleCTAClick = (slide) => {
        trackServiceInquiry(slide.service);
        navigate(slide.ctaLink);
    };

    const handleLearnMoreClick = (service) => {
        trackServiceInquiry('Learn More - ' + service);
        // Navigate to services page for general info, or specific service if available
        if (service === 'Insulation') {
            navigate('/insulation');
        } else if (service === 'Healthy Home Inspection') {
            navigate('/healthy-home');
        } else if (service === 'Garage Doors') {
            navigate('/garage-door');
        } else {
            navigate('/insulation');
        }
    };

    return (
        <>
            {/* Sticky Navbar - appears when scrolled with smooth animation */}
            <AnimatePresence>
                {isScrolled && (
                    <motion.div
                        className="fixed top-0 left-0 right-0 z-50"
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -100, opacity: 0 }}
                        transition={{
                            duration: 0.3,
                            ease: "easeOut",
                            type: "spring",
                            stiffness: 300,
                            damping: 30
                        }}
                    >
                        <Navbar isOverlay={false} isScrolled={true} />
                    </motion.div>
                )}
            </AnimatePresence>

            <section className="relative h-screen overflow-hidden">
                {/* Navbar Overlay - appears when not scrolled with smooth animation */}
                <AnimatePresence>
                    {!isScrolled && (
                        <motion.div
                            className="absolute top-0 left-0 right-0 z-30"
                            initial={{ opacity: 1 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: 0.2,
                                ease: "easeInOut"
                            }}
                        >
                            <Navbar isOverlay={true} isScrolled={false} />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Slides Container */}
                <div className="relative h-full">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentSlide}
                            className="absolute inset-0"
                            initial={{ opacity: 0, scale: 1.1 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.8, ease: "easeInOut" }}
                        >
                            {/* Background Image */}
                            <div
                                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                                style={{
                                    backgroundImage: `url(${slides[currentSlide].image})`,
                                }}
                            />

                            {/* Dark Overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-50" />

                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                        </motion.div>
                    </AnimatePresence>

                    {/* Content */}
                    <div className="relative z-10 h-full flex items-center">
                        <div className="max-w-7xl mx-auto px-4 md:px-4 w-full">
                            <div className="max-w-3xl mx-4 md:mx-0">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentSlide}
                                        initial={{ opacity: 0, y: 50 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -50 }}
                                        transition={{ duration: 0.6, delay: 0.2 }}
                                    >
                                        <motion.h1
                                            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 md:mb-6"
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.8, delay: 0.3 }}
                                        >
                                            {slides[currentSlide].title}
                                        </motion.h1>

                                        <motion.h2
                                            className="text-lg sm:text-xl md:text-2xl text-brand-orange font-semibold mb-3 md:mb-4"
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.8, delay: 0.4 }}
                                        >
                                            {slides[currentSlide].subtitle}
                                        </motion.h2>

                                        <motion.p
                                            className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed mb-6 md:mb-8 max-w-2xl"
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.8, delay: 0.5 }}
                                        >
                                            {slides[currentSlide].description}
                                        </motion.p>

                                        <motion.div
                                            className="flex flex-col sm:flex-row gap-3 md:gap-4"
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.8, delay: 0.6 }}
                                        >
                                            <button
                                                onClick={() => handleCTAClick(slides[currentSlide])}
                                                className="bg-brand-orange text-white px-6 py-3 md:px-8 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:bg-brand-orange-hover transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                            >
                                                {slides[currentSlide].ctaText}
                                            </button>
                                            <button
                                                onClick={() => handleLearnMoreClick(slides[currentSlide].service)}
                                                className="border-2 border-white text-white px-6 py-3 md:px-8 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:bg-white hover:text-black transition-colors duration-300"
                                            >
                                                Learn More
                                            </button>
                                        </motion.div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Arrows */}
                    <button
                        onClick={goToPrevious}
                        className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-2 md:p-3 rounded-full transition-colors duration-300"
                        aria-label="Previous slide"
                    >
                        <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        onClick={goToNext}
                        className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-2 md:p-3 rounded-full transition-colors duration-300"
                        aria-label="Next slide"
                    >
                        <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Dots Navigation */}
                    <div className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2 md:space-x-3">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-colors duration-300 ${index === currentSlide
                                    ? 'bg-brand-orange'
                                    : 'bg-white/50 hover:bg-white/70'
                                    }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>

                    {/* Slide Counter */}
                    <div className="absolute bottom-6 md:bottom-8 right-4 md:right-8 z-20 text-white/70 text-xs md:text-sm">
                        {currentSlide + 1} / {slides.length}
                    </div>
                </div>
            </section>
        </>
    );
};

export default HeroSlider;
